import React from 'react';
import useSheet from 'app/jss';
import classNames from 'classnames';
import { range, noop, get } from 'lodash';
import Icon from 'app/components/commons/Icon';
import Tappable from 'app/components/commons/Tappable';

type Props = {
  classes: Object,
  pages: number,
  currentPage: number,
  onPageChange?: (page: number) => void,
  children?: any,
};

type State = {};


class CarouselPaginationDesktop extends React.Component {
  props: Props;
  state: State = {};

  static defaultProps = {
    children: undefined,
    onPageChange: noop,
  }

  nextPage = () => {
    const { currentPage, pages, onPageChange } = this.props;
    onPageChange(Math.min(currentPage + 1, pages));
  }

  previousPage = () => {
    const { currentPage, onPageChange } = this.props;
    onPageChange(Math.max(currentPage - 1, 1));
  }

  setPage = (page: number) => {
    this.props.onPageChange(page);
  }

  buildPageBubbles() {
    const {
      classes,
      pages,
      currentPage,
    } = this.props;
    const val = 1;
    return range(1, pages + 1).map((pageNumber) => {
      const isActive = currentPage === pageNumber;
      return (
        <Tappable
          className={classes.pageBubbleHitbox}
          key={pageNumber}
          onTap={() => this.setPage(pageNumber)}
        >
          <div className={classNames(classes.pageBubble, { active: isActive })} />
        </Tappable>);
    });
  }


  render() {
    const {
      classes,
      pages,
      currentPage,
      children,
    } = this.props;

    const canGoLeft = currentPage !== 1;
    const canGoRight = currentPage < pages;
    const style = {
      transform: `translateX(calc(-${(currentPage - 1) * 100}% - ${(currentPage - 1) * 20}px))`,
    };
    return (
      <div className={classes.carouselContainer}>
        <div className={classes.carouselWrapper}>
          <div className={classNames(classes.carousel)} style={style}>
            {children}
          </div>
        </div>
        {pages > 1 && (
          <div>
            <Tappable
              className={classNames(classes.paginationIndicator, 'left', { disabled: !canGoLeft })}
              onTap={(canGoLeft && this.previousPage) || noop}
            >
              <Icon type="icon-left-arrow" />
            </Tappable>
            <Tappable
              className={classNames(classes.paginationIndicator, 'right', { disabled: !canGoRight })}
              onTap={(canGoRight && this.nextPage) || noop}
            >
              <Icon type="icon-right-arrow" />
            </Tappable>
            <div className={classes.pageBubbles}>
              {this.buildPageBubbles()}
            </div>
          </div>)
        }
      </div>
    );
  }
}

function applyStyles(theme: Theme) {
  return {
    carouselContainer: {
      position: 'relative',
      // no need for css utils hover, this is only for desktop!
      '&:hover': {
        '& $paginationIndicator': {
          pointerEvents: 'auto',
          opacity: 1,
          '&.right': {
            transform: 'translateX(33%)',
          },
          '&.left': {
            transform: 'translateX(-33%)',
          },
          '&.disabled': {
            opacity: 0.5,
          },
        },
      },
    },
    carouselWrapper: {
      overflow: 'hidden',
    },
    paginationIndicator: {
      position: 'absolute',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      top: 'calc(50% - 30px)',
      opacity: 0,
      zIndex: 1,
      transition: [
        'opacity 0.3s ease-in',
        'transform 0.3s ease-out',
        'color 0.2s linear',
        'background-color 0.2s linear',
      ].join(', '),
      color: theme.color.basis4,
      backgroundColor: theme.color.basis3,
      height: 30,
      width: 30,
      borderRadius: '100%',
      '&.right': {
        right: 0,
        transform: 'translateX(30px)',
      },
      '&.left': {
        left: 0,
        transform: 'translateX(-30px)',
      },

      pointerEvents: 'none',
      '&:hover:not(.disabled)': {
        pointerEvents: 'auto',
        color: theme.color.white,
        backgroundColor: theme.color.basis6,
      },
    },
    pageBubble: {
      height: 5,
      width: 5,
      borderRadius: '100%',
      backgroundColor: theme.color.basis3,
      transition: 'background-color 0.2s, height 0.2s, width 0.2s',
      '&.active': {
        height: 10,
        width: 10,
        backgroundColor: theme.color.basis6,
      },
    },
    pageBubbleHitbox: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: 15,
      width: 25,
    },
    pageBubbles: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginTop: 25,
    },
    carousel: {
      display: 'inline-flex',
      width: '100%',
      transition: 'transform 0.8s cubic-bezier(0.455, 0.03, 0.515, 0.955)',
      '& >*': {
        paddingRight: 20,
      },
    },
  };
}

export default useSheet(applyStyles)(CarouselPaginationDesktop);
